import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { profileAPI, followAPI } from '../services/api';
import { updateProfileMeta } from '../utils/metaUtils';
import trackingService from '../services/tracking';
import MusicUploadModal from '../components/MusicUploadModal';
import VideoUploadModal from '../components/VideoUploadModal';
import ImageUploadModal from '../components/ImageUploadModal';
import PostCreator from '../components/PostCreator';
import PostsFeed from '../components/PostsFeed';
import PageHeader from '../components/PageHeader';
import ShareModal from '../components/ShareModal';
import AudioPlayer from '../components/AudioPlayer';
import AccessRequiredModal from '../components/AccessRequiredModal';
import SignupInviteModal from '../components/SignupInviteModal';
import AccessLevelBadge from '../components/AccessLevelBadge';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { getArtistTypeLabel } from '../utils/artistTypes';
import {
    User, MapPin, Calendar, Edit, Settings, Music,
    ExternalLink, Instagram, Globe, Loader, AlertCircle,
    Star, Verified, Heart, Play, Share2, MoreHorizontal,
    Users, Music2, Upload, Video, Plus, Pause, Trash2, X,
    Twitter, Youtube, Facebook, Linkedin, Github, Eye,
    ImageIcon, MessageCircle, Clock, Crown, Palette, Paintbrush, ChevronDown, Disc3
} from 'lucide-react';

// Links Management Modal Component
const LinksModal = ({ isOpen, onClose, onSave, currentLinks }) => {
    const [links, setLinks] = useState([]);
    const [saving, setSaving] = useState(false);

    const linkTypes = [
        { value: 'website', label: 'Website', icon: Globe },
        { value: 'instagram', label: 'Instagram', icon: Instagram },
        { value: 'twitter', label: 'Twitter', icon: Twitter },
        { value: 'youtube', label: 'YouTube', icon: Youtube },
        { value: 'facebook', label: 'Facebook', icon: Facebook },
        { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
        { value: 'github', label: 'GitHub', icon: Github },
        { value: 'tiktok', label: 'TikTok', icon: Video },
        { value: 'spotify', label: 'Spotify', icon: Music },
        { value: 'apple_music', label: 'Apple Music', icon: Music2 },
        { value: 'soundcloud', label: 'SoundCloud', icon: Music }
    ];

    useEffect(() => {
        if (isOpen) {
            setLinks(currentLinks.length > 0 ? [...currentLinks] : [{ type: 'website', label: '', url: '' }]);
        }
    }, [isOpen, currentLinks]);

    const addLink = () => {
        setLinks([...links, { type: 'website', label: '', url: '' }]);
    };

    const removeLink = (index) => {
        setLinks(links.filter((_, i) => i !== index));
    };

    const updateLink = (index, field, value) => {
        const newLinks = [...links];
        newLinks[index] = { ...newLinks[index], [field]: value };
        
        // Auto-fill label when type changes
        if (field === 'type') {
            const typeConfig = linkTypes.find(t => t.value === value);
            if (typeConfig && !newLinks[index].label) {
                newLinks[index].label = typeConfig.label;
            }
        }
        
        setLinks(newLinks);
    };

    const handleSave = async () => {
        const validLinks = links.filter(link => link.url && link.label);
        
        if (validLinks.length === 0) {
            await onSave([]);
            return;
        }

        // Validate URLs
        for (const link of validLinks) {
            try {
                new URL(link.url.startsWith('http') ? link.url : `https://${link.url}`);
            } catch {
                alert(`Please enter a valid URL for ${link.label}`);
                return;
            }
        }

        setSaving(true);
        const success = await onSave(validLinks.map(link => ({
            ...link,
            url: link.url.startsWith('http') ? link.url : `https://${link.url}`
        })));
        
        if (!success) {
            alert('Failed to save links. Please try again.');
        }
        setSaving(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <h3 className="text-xl font-bold text-white">Manage Links</h3>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-4">
                        {links.map((link, index) => {
                            const selectedType = linkTypes.find(t => t.value === link.type);
                            return (
                                <div key={index} className="bg-gray-700/30 rounded-lg p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-gray-300">Link #{index + 1}</h4>
                                        {links.length > 1 && (
                                            <button
                                                onClick={() => removeLink(index)}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                                            <select
                                                value={link.type}
                                                onChange={(e) => updateLink(index, 'type', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                            >
                                                {linkTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-1">Label</label>
                                            <input
                                                type="text"
                                                value={link.label}
                                                onChange={(e) => updateLink(index, 'label', e.target.value)}
                                                placeholder="Display name"
                                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-1">URL</label>
                                        <input
                                            type="url"
                                            value={link.url}
                                            onChange={(e) => updateLink(index, 'url', e.target.value)}
                                            placeholder="https://example.com"
                                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                        />
                                    </div>
                                    
                                    {selectedType && link.label && link.url && (
                                        <div className="pt-2 border-t border-gray-600">
                                            <p className="text-xs text-gray-400 mb-2">Preview:</p>
                                            <div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded text-sm ${
                                                link.type === 'instagram' ? 'bg-gradient-to-r from-blue-500/20 to-blue-700/20 text-blue-400' :
                                                link.type === 'twitter' ? 'bg-sky-500/20 text-sky-400' :
                                                link.type === 'youtube' ? 'bg-red-500/20 text-red-400' :
                                                link.type === 'spotify' ? 'bg-green-500/20 text-green-400' :
                                                link.type === 'apple_music' ? 'bg-pink-500/20 text-pink-400' :
                                                'bg-blue-500/20 text-blue-400'
                                            }`}>
                                                <selectedType.icon className="w-3 h-3" />
                                                <span>{link.label}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    <button
                        onClick={addLink}
                        className="mt-4 w-full px-4 py-2 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-blue-600 hover:text-blue-400 transition-all duration-300 flex items-center justify-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Add Another Link
                    </button>
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-gray-700">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Link className="w-4 h-4" />
                                Save Links
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProfilePage = () => {
    const { username } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated, hasActiveSubscription, subscriptionPlan } = useAuth();
    
    const [profile, setProfile] = useState(null);
    const [profileStats, setProfileStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);
    const [followerCount, setFollowerCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);
    const [albums, setAlbums] = useState([]);
    const [albumsLoading, setAlbumsLoading] = useState(false);
    const [albumLikes, setAlbumLikes] = useState({});
    const [deletingAlbumId, setDeletingAlbumId] = useState(null);
    const [albumShareModal, setAlbumShareModal] = useState({ isOpen: false, album: null });
    const [musicUploads, setMusicUploads] = useState([]);
    const [videoUploads, setVideoUploads] = useState([]);
    const [imageUploads, setImageUploads] = useState([]);
    const [postsTotal, setPostsTotal] = useState(0);
    const [likedMedia, setLikedMedia] = useState([]);
    const [likedMediaTotal, setLikedMediaTotal] = useState(0);
    const [likedMediaLoading, setLikedMediaLoading] = useState(false);
    const [uploadsLoading, setUploadsLoading] = useState(false);
    const [musicStats, setMusicStats] = useState({});
    const [showMusicUploadModal, setShowMusicUploadModal] = useState(false);
    const [showVideoUploadModal, setShowVideoUploadModal] = useState(false);
    const [showImageUploadModal, setShowImageUploadModal] = useState(false);
    const [showUploadMenu, setShowUploadMenu] = useState(false);
    const [shareModal, setShareModal] = useState({ isOpen: false, profile: null });
    const [accessModal, setAccessModal] = useState({ isOpen: false, item: null, mediaType: 'audio' });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'content' });
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, item: null, type: null });
    const [deleting, setDeleting] = useState(false);
    const [showLinksModal, setShowLinksModal] = useState(false);
    const [profileLinks, setProfileLinks] = useState([]);
    const [linksLoading, setLinksLoading] = useState(false);
    const [refreshPosts, setRefreshPosts] = useState(Date.now());
    const [showArtTypes, setShowArtTypes] = useState(false);
    const [uploadLikes, setUploadLikes] = useState({});
    const [videoDurations, setVideoDurations] = useState({});
    const [mediaShareModal, setMediaShareModal] = useState({ isOpen: false, upload: null, type: null });
    const [userBadges, setUserBadges] = useState([]);
    const [badgesLoading, setBadgesLoading] = useState(false);
    const [followingList, setFollowingList] = useState([]);
    const [followingLoading, setFollowingLoading] = useState(false);
    const [showFollowingModal, setShowFollowingModal] = useState(false);
    const [followingModalSearch, setFollowingModalSearch] = useState('');
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [profileHasMembership, setProfileHasMembership] = useState(false);
    const [profileMembershipPlan, setProfileMembershipPlan] = useState(null);

    // Check if this is the current user's profile
    const isOwnProfile = user?.profile?.username === username;

    // Close the "Add Content" upload dropdown when clicking outside of it
    const uploadMenuRef = useRef(null);
    useEffect(() => {
        if (!showUploadMenu) return;
        const handleClickOutside = (event) => {
            if (uploadMenuRef.current && !uploadMenuRef.current.contains(event.target)) {
                setShowUploadMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showUploadMenu]);

    // Load profile data
    useEffect(() => {
        const loadProfile = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await profileAPI.getProfile(username);
                setProfile(response.profile);
                
                // Update page meta tags for SEO and social sharing
                if (response.profile) {
                    updateProfileMeta(response.profile).catch(console.error);
                }
                
                // Set follower/following counts from profile
                setFollowerCount(response.profile?.follower_count || 0);
                setFollowingCount(response.profile?.following_count || 0);

                // Check follow status if viewing another user's profile and user is authenticated
                if (isAuthenticated && !isOwnProfile && response.profile?.user_id) {
                    try {
                        const followStatus = await followAPI.checkFollowStatus(response.profile.user_id);
                        if (followStatus.success) {
                            setIsFollowing(followStatus.isFollowing);
                        }
                    } catch (error) {
                        console.error('Error checking follow status:', error);
                    }
                }

                // Track page view (don't track for own profile)
                if (user?.profile?.username !== username) {
                    trackingService.trackPageView('profile', username);
                }

                // Load profile statistics
                const stats = await trackingService.getProfileStats(username);
                if (stats && stats.success) {
                    setProfileStats(stats.data);
                }
            } catch (error) {
                setError(error?.message || 'Failed to load profile');
                if (error?.status === 404) {
                    navigate('/404');
                }
            } finally {
                setLoading(false);
            }
        };

        if (username) {
            loadProfile();
        }
    }, [username, navigate, isAuthenticated, isOwnProfile]);

    // Load uploads when profile loads
    useEffect(() => {
        if (profile) {
            if (isOwnProfile && isAuthenticated) {
                loadUserUploads(); // Load private uploads for own profile
            } else {
                loadPublicUploads(); // Load public uploads for other profiles
            }
        }
    }, [profile, isOwnProfile, isAuthenticated]);

    // Load albums when profile loads
    useEffect(() => {
        const loadAlbums = async () => {
            if (!profile) return;
            try {
                setAlbumsLoading(true);
                const response = isOwnProfile && isAuthenticated
                    ? await api.get('/albums/mine')
                    : await api.get(`/albums/user/${username}`);
                if (response.data.success) {
                    setAlbums(response.data.data);
                }
            } catch (error) {
                console.error('Error loading albums:', error);
            } finally {
                setAlbumsLoading(false);
            }
        };
        loadAlbums();
    }, [profile, isOwnProfile, isAuthenticated, username]);

    // Load the profile's most recently liked media (same for own or other profiles)
    useEffect(() => {
        if (!profile?.username) return;
        const loadLikedMedia = async () => {
            try {
                setLikedMediaLoading(true);
                const response = await fetch(`/api/upload/liked/${profile.username}?limit=6`);
                if (response.ok) {
                    const data = await response.json();
                    setLikedMedia(data.data || []);
                    setLikedMediaTotal(data.total || 0);
                } else {
                    setLikedMedia([]);
                    setLikedMediaTotal(0);
                }
            } catch (error) {
                console.error('Error loading liked media:', error);
                setLikedMedia([]);
                setLikedMediaTotal(0);
            } finally {
                setLikedMediaLoading(false);
            }
        };
        loadLikedMedia();
    }, [profile?.username]);

    // Load profile links when profile is loaded
    useEffect(() => {
        if (profile && username) {
            loadProfileLinks();
            loadUserBadges();
            loadFollowingList();
            loadProfileMembership();
        }
    }, [profile, username]);

    // Keep own-profile membership in sync with auth context
    useEffect(() => {
        if (isOwnProfile) {
            setProfileHasMembership(hasActiveSubscription);
            setProfileMembershipPlan(subscriptionPlan);
        }
    }, [isOwnProfile, hasActiveSubscription, subscriptionPlan]);

    const loadProfileMembership = async () => {
        if (!profile?.user_id) return;
        if (isOwnProfile) {
            setProfileHasMembership(hasActiveSubscription);
            setProfileMembershipPlan(subscriptionPlan);
            return;
        }
        try {
            const response = await fetch(`/api/users/subscription-status/${profile.user_id}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setProfileHasMembership(data.hasMembership);
                    setProfileMembershipPlan(data.planName ? { plan_name: data.planName } : null);
                }
            }
        } catch {
            // Non-critical — silently fail
        }
    };

    // Load user uploads (private uploads for own profile)
    const loadUserUploads = async () => {
        
        try {
            setUploadsLoading(true);
            const token = localStorage.getItem('deepwave_token');
            
            // Load music uploads
            const musicResponse = await fetch('/api/upload/my/audio', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (musicResponse.ok) {
                const musicData = await musicResponse.json();
                const uploads = musicData.data || [];
                setMusicUploads(uploads);
                
                // Load stats for each music upload
                const statsPromises = uploads.map(async (upload) => {
                    const stats = await trackingService.getMediaStats(upload.id);
                    return { uploadId: upload.id, stats };
                });
                
                const statsResults = await Promise.all(statsPromises);
                const statsMap = {};
                statsResults.forEach(({ uploadId, stats }) => {
                    if (stats?.success) {
                        statsMap[uploadId] = stats.data;
                    }
                });
                setMusicStats(statsMap);
            }

            // Load video uploads
            const videoResponse = await fetch('/api/upload/my/video', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (videoResponse.ok) {
                const videoData = await videoResponse.json();
                setVideoUploads(videoData.data || []);
            }

            // Load image uploads
            const imageResponse = await fetch('/api/upload/my/image', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (imageResponse.ok) {
                const imageData = await imageResponse.json();
                setImageUploads(imageData.data || []);
            }
        } catch (error) {
            console.error('Error loading uploads:', error);
        } finally {
            setUploadsLoading(false);
        }
    };

    // Load public uploads for viewing other users' profiles
    const loadPublicUploads = async () => {
        if (!profile?.username) return;

        try {
            setUploadsLoading(true);

            // Attach the viewer's token (if logged in) so the backend can report
            // their own like status on each item — omitted entirely for guests.
            const token = localStorage.getItem('deepwave_token');
            const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

            // Load public music uploads for this user
            const musicResponse = await fetch(`/api/upload/public/audio?username=${profile.username}`, {
                headers: authHeaders
            });

            if (musicResponse.ok) {
                const musicData = await musicResponse.json();
                const uploads = musicData.data || [];
                setMusicUploads(uploads);
                
                // Load stats for each music upload
                const statsPromises = uploads.map(async (upload) => {
                    const stats = await trackingService.getMediaStats(upload.id);
                    return { uploadId: upload.id, stats };
                });
                
                const statsResults = await Promise.all(statsPromises);
                const statsMap = {};
                statsResults.forEach(({ uploadId, stats }) => {
                    if (stats?.success) {
                        statsMap[uploadId] = stats.data;
                    }
                });
                setMusicStats(statsMap);
            } else {
                setMusicUploads([]);
                setMusicStats({});
            }

            // Load public video uploads for this user
            const videoResponse = await fetch(`/api/upload/public/video?username=${profile.username}`, {
                headers: authHeaders
            });

            if (videoResponse.ok) {
                const videoData = await videoResponse.json();
                setVideoUploads(videoData.data || []);
            } else {
                setVideoUploads([]);
            }

            // Load public image uploads for this user
            const imageResponse = await fetch(`/api/upload/public/image?username=${profile.username}`, {
                headers: authHeaders
            });

            if (imageResponse.ok) {
                const imageData = await imageResponse.json();
                setImageUploads(imageData.data || []);
            } else {
                setImageUploads([]);
            }
        } catch (error) {
            console.error('Error loading public uploads:', error);
            setMusicUploads([]);
            setVideoUploads([]);
            setImageUploads([]);
        } finally {
            setUploadsLoading(false);
        }
    };

    // Handle successful music upload
    const handleMusicUploadSuccess = () => {
        setShowMusicUploadModal(false);
        loadUserUploads(); // Refresh the uploads list
    };

    // Handle successful video upload
    const handleVideoUploadSuccess = () => {
        setShowVideoUploadModal(false);
        loadUserUploads(); // Refresh the uploads list
    };

    // Handle successful image upload
    const handleImageUploadSuccess = () => {
        setShowImageUploadModal(false);
        loadUserUploads(); // Refresh the uploads list
    };

    // Handle video playback
    const handleVideoPlay = (upload) => {
        // Stop tracking for any currently playing media
        if (currentlyPlaying && currentlyPlaying !== upload.id) {
            trackingService.stopPlayTracking(currentlyPlaying);
        }

        // For videos, we'll show them in a modal or expand them inline
        const videoElement = document.getElementById(`video-${upload.id}`);
        if (videoElement) {
            if (currentlyPlaying === upload.id) {
                videoElement.pause();
                setCurrentlyPlaying(null);
                trackingService.stopPlayTracking(upload.id);
            } else {
                // Pause any other videos
                if (currentlyPlaying) {
                    const currentVideo = document.getElementById(`video-${currentlyPlaying}`);
                    if (currentVideo) {
                        currentVideo.pause();
                    }
                }
                videoElement.play();
                setCurrentlyPlaying(upload.id);
                
                // Start tracking this video play
                trackingService.startPlayTracking(upload.id, videoElement);
                
                // Note: Play count is handled by the backend tracking service
            }
        }
    };

    // Handle follow/unfollow
    const handleFollowToggle = async () => {
        if (!isAuthenticated) {
            setShowLoginModal(true);
            return;
        }

        if (!profile?.user_id || followLoading) {
            return;
        }

        try {
            setFollowLoading(true);
            
            if (isFollowing) {
                // Unfollow user
                const result = await followAPI.unfollowUser(profile.user_id);
                if (result.success) {
                    setIsFollowing(false);
                    setFollowerCount(prev => Math.max(0, prev - 1));
                }
            } else {
                // Follow user
                const result = await followAPI.followUser(profile.user_id);
                if (result.success) {
                    setIsFollowing(true);
                    setFollowerCount(prev => prev + 1);
                }
            }
        } catch (error) {
            console.error('Follow error:', error);
            // Show user-friendly error message
            if (error.response?.data?.message) {
                alert(error.response.data.message);
            } else {
                alert('Failed to update follow status. Please try again.');
            }
        } finally {
            setFollowLoading(false);
        }
    };

    // Handle delete confirmation
    const handleDeleteClick = (item, type) => {
        setDeleteModal({ show: true, item, type });
    };

    // Music/Video/Image tabs also include uploads where this profile's user was
    // tagged as a contributing artist (not just uploads they own) — those rows carry
    // the original uploader's username, so gating/delete controls must check real
    // ownership per-item instead of just "am I viewing my own profile".
    const isOwnUpload = (upload) => isOwnProfile && upload.username === profile?.username;

    // True when this upload belongs to someone else but this profile's user was tagged
    // as a contributing artist on it — shown as a "Contributor" pill instead of implying
    // they're the original poster.
    const isTaggedUpload = (upload) => upload.username !== profile?.username;

    // Handle delete execution
    const handleDeleteConfirm = async () => {
        if (!deleteModal.item) return;

        setDeleting(true);
        try {
            const token = localStorage.getItem('deepwave_token');
            const response = await fetch(`/api/upload/${deleteModal.item.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                // Remove from the appropriate list
                if (deleteModal.type === 'audio') {
                    setMusicUploads(prev => prev.filter(upload => upload.id !== deleteModal.item.id));
                } else if (deleteModal.type === 'video') {
                    setVideoUploads(prev => prev.filter(upload => upload.id !== deleteModal.item.id));
                } else if (deleteModal.type === 'image') {
                    setImageUploads(prev => prev.filter(upload => upload.id !== deleteModal.item.id));
                }
                
                // Close modal
                setDeleteModal({ show: false, item: null, type: null });
            } else {
                console.error('Failed to delete media');
                alert('Failed to delete media. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting media:', error);
            alert('Error deleting media. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    // Handle delete cancel
    const handleDeleteCancel = () => {
        setDeleteModal({ show: false, item: null, type: null });
    };

    // Handle share profile
    const handleShare = () => {
        setShareModal({ isOpen: true, profile });
    };

    // Handle like on a video or image upload
    const handleUploadLike = async (upload) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: upload.file_type || 'content' });
            return;
        }
        try {
            const token = localStorage.getItem('deepwave_token');
            const res = await fetch(`/api/upload/${upload.id}/like`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setUploadLikes(prev => ({
                        ...prev,
                        [upload.id]: { liked: data.data.liked, count: data.data.likeCount }
                    }));
                }
            }
        } catch (err) {
            console.error('Error liking upload:', err);
        }
    };

    const handleAlbumLike = async (album) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'album' });
            return;
        }
        try {
            const response = await api.post(`/albums/${album.id}/like`);
            if (response.data.success) {
                setAlbumLikes(prev => ({
                    ...prev,
                    [album.id]: { liked: response.data.data.liked, count: response.data.data.likeCount }
                }));
            }
        } catch (err) {
            console.error('Error liking album:', err);
        }
    };

    const handleDeleteAlbum = async (album) => {
        if (!window.confirm(`Delete the album "${album.title}"? This cannot be undone.`)) return;
        setDeletingAlbumId(album.id);
        try {
            await api.delete(`/albums/${album.id}`);
            setAlbums(prev => prev.filter(a => a.id !== album.id));
        } catch (err) {
            console.error('Error deleting album:', err);
        } finally {
            setDeletingAlbumId(null);
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds)) return null;
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // Load profile links
    const loadProfileLinks = async () => {
        if (!username) {
            return;
        }
        
        setLinksLoading(true);
        try {
            const response = await fetch(`/api/profile/${username}/links`);
            if (response.ok) {
                const result = await response.json();
                const linksData = result.data || [];
                setProfileLinks(linksData);
            } else {
                console.error('Failed to load links:', await response.text());
            }
        } catch (error) {
            console.error('Error loading profile links:', error);
        } finally {
            setLinksLoading(false);
        }
    };

    // Save profile links
    const saveProfileLinks = async (links) => {
        try {
            const token = localStorage.getItem('deepwave_token');
            const response = await fetch(`/api/profile/links`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ links })
            });

            if (response.ok) {
                setShowLinksModal(false);
                loadProfileLinks(); // Reload the links from database
                alert('Links saved successfully!');
                return true;
            } else {
                const errorData = await response.text();
                console.error('Failed to save links:', response.status, errorData);
                alert(`Failed to save links: ${response.status} - ${errorData}`);
                return false;
            }
        } catch (error) {
            console.error('Error saving links:', error);
            return false;
        }
    };

    // Load following list
    const loadFollowingList = async () => {
        if (!profile?.user_id) {
            return;
        }
        
        setFollowingLoading(true);
        try {
            // Only show following list for own profile or if profile is public
            if (isOwnProfile) {
                // Load own following list
                const response = await followAPI.getFollowing(profile.user_id);
                if (response.data.success) {
                    setFollowingList(response.data.following || []);
                }
            } else {
                // For other users' profiles, we might want to show a limited view
                // For now, leave empty for privacy
                setFollowingList([]);
            }
        } catch (error) {
            console.error('Error loading following list:', error);
            setFollowingList([]);
        } finally {
            setFollowingLoading(false);
        }
    };

    // Load user badges
    const loadUserBadges = async () => {
        if (!username) {
            return;
        }
        
        setBadgesLoading(true);
        try {
            // For own profile, use my-badges endpoint like dashboard
            if (isOwnProfile && user) {
                const response = await fetch('/api/badges/my-badges', {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                    }
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.success) {
                        setUserBadges(result.data || []);
                    }
                }
            } else {
                // For other profiles, get user ID from username first
                const userResponse = await fetch(`/api/profile/${username}`);
                if (userResponse.ok) {
                    const userResult = await userResponse.json();
                    const userId = userResult.profile?.user_id;
                    
                    if (userId) {
                        const badgesResponse = await fetch(`/api/badges/user/${userId}`);
                        if (badgesResponse.ok) {
                            const badgesResult = await badgesResponse.json();
                            if (badgesResult.success) {
                                setUserBadges(badgesResult.data || []);
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error loading user badges:', error);
        } finally {
            setBadgesLoading(false);
        }
    };

    // Get icon for link type
    const getLinkIcon = (type) => {
        const icons = {
            website: Globe,
            instagram: Instagram,
            twitter: Twitter,
            youtube: Youtube,
            facebook: Facebook,
            linkedin: Linkedin,
            github: Github,
            tiktok: Video,
            spotify: Music,
            apple_music: Music2,
            soundcloud: Music,
            default: Link
        };
        return icons[type] || icons.default;
    };

    // Get platform color
    const getLinkColor = (type) => {
        const colors = {
            website: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30',
            instagram: 'bg-gradient-to-r from-blue-500/20 to-blue-700/20 text-blue-400 hover:from-blue-500/30 hover:to-blue-700/30',
            twitter: 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30',
            youtube: 'bg-red-500/20 text-red-400 hover:bg-red-500/30',
            facebook: 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30',
            linkedin: 'bg-blue-700/20 text-blue-400 hover:bg-blue-700/30',
            github: 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30',
            tiktok: 'bg-black/20 text-white hover:bg-black/30',
            spotify: 'bg-green-500/20 text-green-400 hover:bg-green-500/30',
            apple_music: 'bg-pink-500/20 text-pink-400 hover:bg-pink-500/30',
            soundcloud: 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30',
            default: 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'
        };
        return colors[type] || colors.default;
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-400">Loading profile...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
                    <p className="text-gray-400 mb-6">{error}</p>
                    <button 
                        onClick={() => navigate('/')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    // Filter following list for modal search
    const filteredFollowingList = followingList.filter(user => 
        user.username.toLowerCase().includes(followingModalSearch.toLowerCase()) ||
        user.display_name.toLowerCase().includes(followingModalSearch.toLowerCase())
    );

    if (!profile) {
        return null;
    }

    return (
        <>
            {/* Login Modal */}
            {showLoginModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md p-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Heart className="w-8 h-8 text-purple-500" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Follow {profile?.displayName || profile?.username}?</h3>
                            <p className="text-gray-400 mb-6">
                                Sign in to follow your favorite artists and stay updated with their latest content.
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={() => {
                                        setShowLoginModal(false);
                                        navigate('/login');
                                    }}
                                    className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
                                >
                                    Sign In
                                </button>
                                <button
                                    onClick={() => {
                                        setShowLoginModal(false);
                                        navigate('/register');
                                    }}
                                    className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                                >
                                    Create Account
                                </button>
                                <button
                                    onClick={() => setShowLoginModal(false)}
                                    className="w-full px-6 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Following Modal */}
            {showFollowingModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[80vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-700">
                            <h3 className="text-xl font-bold text-white">Following ({followingCount})</h3>
                            <button
                                onClick={() => {
                                    setShowFollowingModal(false);
                                    setFollowingModalSearch('');
                                }}
                                className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors"
                            >
                                <X className="w-4 h-4 text-gray-400" />
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="p-4 border-b border-gray-700">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search following..."
                                    value={followingModalSearch}
                                    onChange={(e) => setFollowingModalSearch(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                    <Users className="w-4 h-4 text-gray-400" />
                                </div>
                            </div>
                        </div>

                        {/* Following List */}
                        <div className="p-4 max-h-[50vh] overflow-y-auto">
                            {followingLoading ? (
                                <div className="text-center py-8">
                                    <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                    <p className="text-gray-400">Loading following...</p>
                                </div>
                            ) : filteredFollowingList.length === 0 ? (
                                <div className="text-center py-8">
                                    <Heart className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                                    <p className="text-gray-400">
                                        {followingModalSearch ? 'No users found matching your search' : 'No users followed yet'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredFollowingList.map((followedUser) => (
                                        <div key={followedUser.user_id} className="bg-gray-700/30 rounded-lg p-4 hover:bg-gray-700/50 transition-all duration-300">
                                            <div className="flex items-center gap-4">
                                                <Link 
                                                    to={`/profile/${followedUser.username}`}
                                                    onClick={() => {
                                                        setShowFollowingModal(false);
                                                        setFollowingModalSearch('');
                                                    }}
                                                    className="flex items-center gap-4 flex-1"
                                                >
                                                    {followedUser.profile_picture_url ? (
                                                        <img
                                                            src={followedUser.profile_picture_url}
                                                            alt={followedUser.display_name}
                                                            width="48"
                                                            height="48"
                                                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                                                            style={{ background: 'transparent' }}
                                                        />
                                                    ) : (
                                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 border-2 border-gray-600 flex items-center justify-center">
                                                            <span className="text-white font-bold">
                                                                {followedUser.display_name?.charAt(0).toUpperCase() || '🌊'}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-semibold text-white truncate">
                                                                {followedUser.display_name}
                                                            </h3>
                                                            {followedUser.artist_verified ? (
                                                                <Verified className="w-4 h-4 text-blue-500 flex-shrink-0" title="Verified Artist" />
                                                            ) : null}
                                                            <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
                                                        </div>
                                                        <p className="text-gray-400 text-sm truncate">
                                                            @{followedUser.username}
                                                        </p>
                                                        {followedUser.crew_role && (
                                                            <p className="text-yellow-400 text-xs font-semibold">
                                                                {followedUser.crew_role}
                                                            </p>
                                                        )}
                                                    </div>
                                                </Link>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {!followingLoading && filteredFollowingList.length > 0 && (
                            <div className="p-4 border-t border-gray-700 text-center">
                                <p className="text-gray-400 text-sm">
                                    {followingModalSearch 
                                        ? `Showing ${filteredFollowingList.length} of ${followingCount} users`
                                        : `${followingCount} users followed`
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

        <div className="min-h-screen bg-gray-900">
            {/* Profile Header with Background Image */}
            <PageHeader
                title={`${profile.displayName || profile.username}'s Profile`}
                subtitle={`You've reached the profile of ${profile.displayName || profile.username} on The Deep Wave`}
                pageType="profile"
                height="h-72"
                showContent={true}
            />
            
            {/* Profile Card — overlaps the bottom of PageHeader */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10" style={{ marginTop: '50px' }}>
                    <div className="glass-card border border-white/10">
                        {/* Avatar + name row */}
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 p-6">
                            {/* Avatar — fully inside the card */}
                            <div className="relative flex-shrink-0">
                                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 p-[3px] shadow-2xl ring-4 ring-gray-900">
                                    <div className="w-full h-full rounded-full overflow-hidden bg-gray-800 flex items-center justify-center">
                                        {profile.profilePictureUrl ? (
                                            <img
                                                src={profile.profilePictureUrl}
                                                alt={profile.displayName}
                                                width="128"
                                                height="128"
                                                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={() => window.open(profile.profilePictureUrl, '_blank')}
                                                title="Click to view full size"
                                            />
                                        ) : (
                                            <span className="text-5xl leading-none">🌊</span>
                                        )}
                                    </div>
                                </div>
                                {!!profile.isArtist && !!profile.artistVerified && (
                                    <div className="absolute bottom-1 right-1 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center ring-2 ring-gray-900">
                                        <Verified className="w-4 h-4 text-white fill-current" />
                                    </div>
                                )}
                            </div>

                            {/* Name + badges + buttons */}
                            <div className="flex-1 w-full min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                    {/* Identity */}
                                    <div className="text-center sm:text-left">
                                        <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                                            <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                                                {profile.displayName}
                                            </h1>
                                            {!!profile.isVerified && (
                                                <Verified className="w-5 h-5 text-blue-400 fill-current flex-shrink-0" />
                                            )}
                                        </div>
                                        {profile.artistName && (
                                            <p className="text-purple-300 text-sm font-medium mt-0.5">{profile.artistName}</p>
                                        )}
                                        <p className="text-gray-400 text-sm mt-0.5">@{profile.username}</p>
                                        <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 flex-wrap">
                                            {!!profile.isCrewMember && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                                                    <Crown className="w-3 h-3" />
                                                    {profile.crewRole || 'Crew'}
                                                </span>
                                            )}
                                            {!!profile.isOfficialArtist && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                                                    <Palette className="w-3 h-3" />
                                                    Official Artist
                                                </span>
                                            )}
                                            {!!profile.isArtist && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600/20 text-blue-300 border border-blue-500/30">
                                                    <Paintbrush className="w-3 h-3" />
                                                    Artist
                                                </span>
                                            )}
                                            {!!profileHasMembership && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                                    ⭐ Supporter
                                                </span>
                                            )}
                                            {!!profile.isArtist && musicUploads.length > 0 && (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-700/60 text-gray-300 border border-gray-600/40">
                                                    <Music2 className="w-3 h-3" />
                                                    {musicUploads.length} tracks
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex items-center justify-center sm:justify-end gap-2 flex-wrap sm:flex-nowrap flex-shrink-0">
                                        <Link
                                            to={`/feed/artist/${profile.username}`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 hover:text-blue-200 text-sm font-medium rounded-full border border-blue-500/30 transition-all duration-200 hover:scale-105"
                                        >
                                            <Users className="w-3.5 h-3.5" />
                                            <span>Feed</span>
                                        </Link>
                                        {imageUploads.length > 0 && (
                                            <Link
                                                to={`/gallery/artist/${profile.username}`}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-300 hover:text-green-200 text-sm font-medium rounded-full border border-green-500/30 transition-all duration-200 hover:scale-105"
                                            >
                                                <ImageIcon className="w-3.5 h-3.5" />
                                                <span>Gallery</span>
                                            </Link>
                                        )}
                                        {isOwnProfile ? (
                                            <>
                                                <div className="relative" ref={uploadMenuRef}>
                                                    <button
                                                        onClick={() => setShowUploadMenu(o => !o)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 hover:text-purple-200 text-sm font-medium rounded-full border border-purple-500/30 transition-all duration-200 hover:scale-105"
                                                    >
                                                        <Upload className="w-3.5 h-3.5" />
                                                        <span>Add Content</span>
                                                        <ChevronDown className={`w-3 h-3 transition-transform ${showUploadMenu ? 'rotate-180' : ''}`} />
                                                    </button>
                                                    {showUploadMenu && (
                                                        <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-44 bg-gray-900 rounded-xl border border-white/10 shadow-2xl py-2 z-20">
                                                            <button
                                                                onClick={() => { setShowUploadMenu(false); setShowImageUploadModal(true); }}
                                                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                                            >
                                                                <ImageIcon className="w-4 h-4 text-green-400" />
                                                                <span>Add Image</span>
                                                            </button>
                                                            <button
                                                                onClick={() => { setShowUploadMenu(false); setShowVideoUploadModal(true); }}
                                                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                                            >
                                                                <Video className="w-4 h-4 text-red-400" />
                                                                <span>Add Video</span>
                                                            </button>
                                                            <button
                                                                onClick={() => { setShowUploadMenu(false); setShowMusicUploadModal(true); }}
                                                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                                            >
                                                                <Music2 className="w-4 h-4 text-purple-400" />
                                                                <span>Add Audio</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => navigate('/edit-profile')}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-700/70 text-gray-200 text-sm font-medium rounded-full border border-gray-600/40 transition-all duration-200 hover:scale-105"
                                                >
                                                    <Edit className="w-3.5 h-3.5" />
                                                    <span>Edit Profile</span>
                                                </button>
                                                <button
                                                    onClick={handleShare}
                                                    className="inline-flex items-center justify-center w-8 h-8 bg-gray-700/50 hover:bg-gray-700/70 text-gray-200 rounded-full border border-gray-600/40 transition-all duration-200 hover:scale-105"
                                                    title="Share Profile"
                                                >
                                                    <Share2 className="w-3.5 h-3.5" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={handleFollowToggle}
                                                    disabled={followLoading}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-200 hover:scale-105 ${
                                                        followLoading
                                                            ? 'bg-gray-600/30 text-gray-400 border-gray-600/30 cursor-not-allowed'
                                                            : isFollowing
                                                            ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/40'
                                                            : 'bg-purple-600/20 text-purple-300 border-purple-500/30 hover:bg-purple-600/30 hover:text-purple-200'
                                                    }`}
                                                >
                                                    {followLoading ? (
                                                        <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <Heart className={`w-3.5 h-3.5 ${isFollowing ? 'fill-current' : ''}`} />
                                                    )}
                                                    <span>
                                                        {followLoading
                                                            ? (isFollowing ? 'Unfollowing...' : 'Following...')
                                                            : isFollowing ? 'Following' : 'Follow'
                                                        }
                                                    </span>
                                                </button>
                                                <button
                                                    onClick={handleShare}
                                                    className="inline-flex items-center justify-center w-8 h-8 bg-gray-700/50 hover:bg-gray-700/70 text-gray-200 rounded-full border border-gray-600/40 transition-all duration-200 hover:scale-105"
                                                    title="Share Profile"
                                                >
                                                    <Share2 className="w-3.5 h-3.5" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bio + meta row */}
                        <div className="px-4 sm:px-6 pb-4 sm:pb-6 border-t border-white/10 pt-4">
                            {profile.bio && (
                                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                                    {profile.bio}
                                </p>
                            )}
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-400">
                                {profile.location && (
                                    <div className="flex items-center gap-1">
                                        <MapPin className="w-3.5 h-3.5" />
                                        <span>{profile.location}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    <span>Joined {new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Link
                                        to={isOwnProfile ? "/following" : `#followers-${profile.username}`}
                                        className="flex items-center gap-1 hover:text-purple-400 transition-colors"
                                    >
                                        <Users className="w-3.5 h-3.5" />
                                        <span>
                                            <strong className="text-white">{followerCount.toLocaleString()}</strong> followers
                                        </span>
                                    </Link>
                                    <Link 
                                        to={isOwnProfile ? "/following" : `#following-${profile.username}`}
                                        className="flex items-center space-x-1 hover:text-purple-400 transition-colors"
                                    >
                                        <Heart className="w-4 h-4" />
                                        <span>
                                            <strong className="text-white">{followingCount.toLocaleString()}</strong> following
                                        </span>
                                    </Link>
                                </div>
                            </div>

                            {/* Profile Links */}
                            {(profileLinks.length > 0 || isOwnProfile) ? (
                                <div className="mt-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-semibold text-gray-300">Links</h3>
                                        {isOwnProfile ? (
                                            <button
                                                onClick={() => setShowLinksModal(true)}
                                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 transition-colors"
                                            >
                                                <Edit className="w-3 h-3" />
                                                {profileLinks.length > 0 ? 'Edit' : 'Add Links'}
                                            </button>
                                        ) : null}
                                    </div>
                                    
                                    {linksLoading ? (
                                        <div className="flex items-center space-x-2">
                                            <Loader className="w-4 h-4 animate-spin text-blue-400" />
                                            <span className="text-gray-400 text-sm">Loading links...</span>
                                        </div>
                                    ) : profileLinks.length > 0 ? (
                                        <div className="flex flex-wrap gap-3">
                                            {profileLinks.map((link, index) => {
                                                const IconComponent = getLinkIcon(link.type);
                                                return (
                                                    <a
                                                        key={index}
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${getLinkColor(link.type)}`}
                                                        title={link.label}
                                                    >
                                                        <IconComponent className="w-4 h-4" />
                                                        <span className="font-medium">{link.label}</span>
                                                        <ExternalLink className="w-3 h-3 opacity-60" />
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    ) : isOwnProfile ? (
                                        <button
                                            onClick={() => setShowLinksModal(true)}
                                            className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-blue-600 hover:text-blue-400 transition-all duration-300"
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span>Add your social links and website</span>
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}

                            {/* Membership Status — hidden for non-members on other people's profiles */}
                            {(profileHasMembership || isOwnProfile) && (
                                <div className="mt-4">
                                    <h3 className="text-sm font-semibold text-gray-300 mb-3">Membership</h3>
                                    {profileHasMembership ? (
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-lg p-3 flex flex-col items-center text-center transition-all duration-300 hover:scale-105">
                                                <div className="text-2xl mb-1">⭐</div>
                                                <div className="text-sm font-semibold text-blue-300 mb-1">
                                                    {profileMembershipPlan?.plan_name || 'Supporter'}
                                                </div>
                                                <div className="text-xs text-gray-400 leading-tight">
                                                    {isOwnProfile ? 'Your active plan' : 'Deep Wave supporter'}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-3">
                                            <div className="text-sm text-gray-400 mb-2">No active membership</div>
                                            <a
                                                href="/subscriptions"
                                                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                                            >
                                                View membership plans →
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* User Badges */}
                            <div className="mt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-gray-300">Badges</h3>
                                    <span className="text-xs text-gray-500">
                                        {badgesLoading ? 'Loading...' : `${userBadges.length} earned`}
                                    </span>
                                </div>
                                
                                {badgesLoading ? (
                                    <div className="flex items-center space-x-2">
                                        <Loader className="w-4 h-4 animate-spin text-blue-400" />
                                        <span className="text-gray-400 text-sm">Loading badges...</span>
                                    </div>
                                ) : userBadges.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        {userBadges.slice(0, 6).map(badge => {
                                            // Map icon names to emojis
                                            const iconMap = {
                                                wave: '🌊',
                                                edit: '📝',
                                                calendar: '🎪',
                                                users: '👥',
                                                music: '🎵',
                                                verified: '✅',
                                                headphones: '🎧',
                                                heart: '🦋',
                                                mic: '🎤',
                                                star: '🏆',
                                                crown: '👑'
                                            };
                                            const displayIcon = iconMap[badge.icon] || '🏅';

                                            const colorSchemes = {
                                                purple: 'from-purple-600/20 to-blue-600/20 border-purple-500/30',
                                                blue: 'from-blue-600/20 to-cyan-600/20 border-blue-500/30',
                                                green: 'from-green-600/20 to-emerald-600/20 border-green-500/30',
                                                yellow: 'from-yellow-600/20 to-orange-600/20 border-yellow-500/30',
                                                pink: 'from-pink-600/20 to-rose-600/20 border-pink-500/30',
                                                gold: 'from-yellow-500/20 to-amber-600/20 border-yellow-400/30'
                                            };
                                            const colorClass = colorSchemes[badge.color_scheme] || colorSchemes.purple;

                                            const textColor = {
                                                purple: 'text-purple-300',
                                                blue: 'text-blue-300',
                                                green: 'text-green-300',
                                                yellow: 'text-yellow-300',
                                                pink: 'text-pink-300',
                                                gold: 'text-yellow-300'
                                            };
                                            const badgeTextColor = textColor[badge.color_scheme] || textColor.purple;

                                            return (
                                                <div 
                                                    key={badge.id} 
                                                    className={`bg-gradient-to-br ${colorClass} border rounded-lg p-3 flex flex-col items-center text-center transition-all duration-300 hover:scale-105`}
                                                    title={`${badge.name}: ${badge.description}${badge.awarded_at ? ` • Earned ${new Date(badge.awarded_at).toLocaleDateString()}` : ''}`}
                                                >
                                                    <div className="text-2xl mb-1">{displayIcon}</div>
                                                    <div className={`text-sm font-semibold ${badgeTextColor} mb-1`}>{badge.name}</div>
                                                    <div className="text-xs text-gray-400 text-center leading-tight">{badge.description}</div>
                                                    {badge.awarded_at && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {new Date(badge.awarded_at).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {userBadges.length > 6 && (
                                            <div className="bg-gray-800/30 border border-gray-600/30 rounded-lg p-3 flex flex-col items-center text-center">
                                                <div className="text-2xl mb-1">⭐</div>
                                                <div className="text-sm font-semibold text-gray-400">+{userBadges.length - 6}</div>
                                                <div className="text-xs text-gray-500">More badges</div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-gray-400 text-sm py-2">
                                        No badges earned yet. Start exploring to earn your first badge!
                                    </div>
                                )}
                            </div>

                            {/* Types of Art — collapsed behind a dropdown pill */}
                            {profile.isArtist && profile.artistTypes?.length > 0 ? (
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowArtTypes(prev => !prev)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-600/20 text-purple-300 border border-purple-500/30 hover:bg-purple-600/30 transition-colors"
                                    >
                                        <Paintbrush className="w-3.5 h-3.5" />
                                        Types of Art
                                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showArtTypes ? 'rotate-180' : ''}`} />
                                    </button>

                                    {showArtTypes && (
                                        <div className="mt-3 space-y-3">
                                            {profile.artistTypes.map((typeKey) => {
                                                const typeGenres = profile.genres?.[typeKey] || [];
                                                return (
                                                    <div key={typeKey}>
                                                        <h3 className="text-sm font-semibold text-gray-300 mb-2">
                                                            {typeKey === 'other' && profile.customArtistType
                                                                ? profile.customArtistType
                                                                : getArtistTypeLabel(typeKey)}
                                                        </h3>
                                                        {typeGenres.length > 0 && (
                                                            <div className="flex flex-wrap gap-2">
                                                                {typeGenres.map((genre) => (
                                                                    <span
                                                                        key={genre}
                                                                        className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-sm"
                                                                    >
                                                                        {genre}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="space-y-4 sm:space-y-8">

                    {/* Posts Section */}
                    <div className="space-y-4 sm:space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl sm:text-3xl font-bold text-white">
                                {isOwnProfile ? 'Posts' : `Posts on ${profile.display_name || profile.username}'s Profile`}
                            </h2>
                        </div>

                        {/* Post Creator - Only show for authenticated users */}
                        <PostCreator 
                            profileUsername={profile.username}
                            onPostCreated={() => setRefreshPosts(Date.now())}
                        />
                        
                        {/* Posts Feed — limited to 2 on profile, full feed linked below */}
                        <PostsFeed
                            profileUsername={profile.username}
                            refreshTrigger={refreshPosts}
                            limit={2}
                            onTotalCount={setPostsTotal}
                        />

                        {postsTotal > 0 && (
                            <div className="text-center pt-2">
                                <Link
                                    to={`/feed/artist/${profile.username}/posts`}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600/10 hover:bg-purple-600/20 text-purple-400 hover:text-purple-300 rounded-lg transition-colors text-sm border border-purple-600/20"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    View All {postsTotal} Posts
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Albums — always shown to the owner (even with zero albums) so
                        "Add Album" is discoverable; hidden for visitors when there's
                        nothing public to show. */}
                    {(isOwnProfile || albumsLoading || albums.length > 0) && (
                        <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-white/10">
                            <div className="flex items-center justify-between mb-4 sm:mb-6">
                                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                                    <Disc3 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                                    {isOwnProfile ? 'My Albums' : 'Albums'}
                                </h2>
                                {isOwnProfile && (
                                    <button
                                        onClick={() => navigate('/edit-profile')}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Add Album</span>
                                        <span className="sm:hidden">Add</span>
                                    </button>
                                )}
                            </div>

                            {albumsLoading ? (
                                <div className="text-center py-8">
                                    <Loader className="w-8 h-8 animate-spin text-purple-500 mx-auto" />
                                </div>
                            ) : albums.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {albums.map((album) => (
                                        <div key={album.id} className="glass-card overflow-hidden hover:scale-105 transition-all duration-300 group">
                                            {/* Cover Image */}
                                            <div className="relative aspect-square bg-gray-900">
                                                {album.cover_image_url ? (
                                                    <img
                                                        src={album.cover_image_url}
                                                        alt={album.title}
                                                        className="w-full h-full object-cover"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-pink-900">
                                                        <Disc3 className="w-12 h-12 text-white/20" />
                                                    </div>
                                                )}

                                                {/* Hover overlay with controls */}
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                                                    <Link
                                                        to={`/album/${album.username}/${album.slug || album.id}`}
                                                        className="p-2 bg-blue-500/80 rounded-lg hover:bg-blue-600 transition-colors"
                                                        title="View Album"
                                                    >
                                                        <Eye className="w-4 h-4 text-white" />
                                                    </Link>
                                                    <button
                                                        onClick={() => window.open(`/album/${album.username}/${album.slug || album.id}`, '_blank')}
                                                        className="p-2 bg-green-500/80 rounded-lg hover:bg-green-600 transition-colors"
                                                        title="Open Album with details"
                                                    >
                                                        <ExternalLink className="w-4 h-4 text-white" />
                                                    </button>
                                                    {isOwnProfile && (
                                                        <button
                                                            onClick={() => handleDeleteAlbum(album)}
                                                            disabled={deletingAlbumId === album.id}
                                                            className="p-2 bg-red-500/80 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                                                            title="Delete Album"
                                                        >
                                                            {deletingAlbumId === album.id ? (
                                                                <Loader className="w-4 h-4 text-white animate-spin" />
                                                            ) : (
                                                                <Trash2 className="w-4 h-4 text-white" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>

                                                <AccessLevelBadge accessLevel={album.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />

                                                {/* Item Count Badge */}
                                                <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                    {album.item_count || 0} item{album.item_count === 1 ? '' : 's'}
                                                </div>
                                            </div>

                                            {/* Album Info */}
                                            <div className="p-3">
                                                <Link to={`/album/${album.username}/${album.slug || album.id}`} className="block">
                                                    <h3 className="font-semibold text-white text-sm truncate mb-1 hover:text-purple-300 transition-colors">
                                                        {album.title}
                                                    </h3>
                                                    <p className="text-xs text-gray-400 mb-2">
                                                        {new Date(album.created_at).toLocaleDateString()}
                                                    </p>
                                                </Link>
                                                <div className="flex items-center gap-4">
                                                    <button
                                                        onClick={() => handleAlbumLike(album)}
                                                        className={`flex items-center gap-1 text-sm transition-colors ${
                                                            (albumLikes[album.id]?.liked ?? album.user_has_liked)
                                                                ? 'text-red-400 hover:text-red-300'
                                                                : 'text-gray-400 hover:text-red-400'
                                                        } cursor-pointer`}
                                                    >
                                                        <Heart className={`w-4 h-4 ${(albumLikes[album.id]?.liked ?? album.user_has_liked) ? 'fill-current' : ''}`} />
                                                        <span>{albumLikes[album.id]?.count ?? album.like_count ?? 0}</span>
                                                    </button>
                                                    <button
                                                        onClick={() => setAlbumShareModal({ isOpen: true, album })}
                                                        className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-400 transition-colors"
                                                        title="Share"
                                                    >
                                                        <Share2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <Disc3 className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-400 mb-4">
                                        {isOwnProfile ? 'No albums yet' : 'No public albums'}
                                    </p>
                                    {isOwnProfile && (
                                        <button
                                            className="inline-flex items-center gap-2 px-4 py-2 mt-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                            onClick={() => navigate('/edit-profile')}
                                        >
                                            <Plus className="w-4 h-4" />
                                            Create Your First Album
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Music Uploads */}
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-white/10">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            <h2 className="text-xl sm:text-2xl font-bold text-white">
                                {isOwnProfile ? 'My Music Uploads' : 'Music'}
                            </h2>
                            <div className="flex items-center gap-3">
                                {isOwnProfile && (
                                    <button
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                        onClick={() => setShowMusicUploadModal(true)}
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Add Music</span>
                                        <span className="sm:hidden">Add</span>
                                    </button>
                                )}
                            </div>
                        </div>
                            
                            {uploadsLoading ? (
                                <div className="text-center py-8">
                                    <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                                    <p className="text-gray-400">Loading uploads...</p>
                                </div>
                            ) : musicUploads.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {musicUploads.map((upload) => (
                                        <div key={upload.id} className="bg-gray-800/50 rounded-xl overflow-hidden hover:bg-gray-700/50 transition-colors">
                                            {(() => {
                                                const gated = isGated(upload.access_level, isAuthenticated, hasActiveSubscription) && !isOwnUpload(upload);
                                                return (
                                                    <div className="relative">
                                                        <AudioPlayer
                                                            src={upload.file_url}
                                                            coverImageUrl={upload.cover_image_url}
                                                            title={upload.title || upload.original_filename}
                                                            uploadId={gated ? null : upload.id}
                                                            locked={gated}
                                                            onLockedClick={() => setAccessModal({ isOpen: true, item: upload, mediaType: 'audio' })}
                                                        />
                                                        {gated && (
                                                            <AccessLevelBadge accessLevel={upload.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            <div className="px-3 pb-3 flex items-center justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="text-white font-semibold text-sm truncate">
                                                            {upload.title || upload.original_filename}
                                                        </h3>
                                                        {isTaggedUpload(upload) && (
                                                            <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                                                Contributor
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-gray-500 text-xs">
                                                        {new Date(upload.created_at).toLocaleDateString()} · {musicStats[upload.id]?.playCount || 0} plays
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <button
                                                        onClick={() => handleUploadLike(upload)}
                                                        title="Like"
                                                        className={`flex items-center gap-1 text-sm transition-colors ${
                                                            (uploadLikes[upload.id]?.liked ?? upload.user_has_liked)
                                                                ? 'text-red-400 hover:text-red-300'
                                                                : 'text-gray-400 hover:text-red-400'
                                                        } cursor-pointer`}
                                                    >
                                                        <Heart className={`w-4 h-4 ${(uploadLikes[upload.id]?.liked ?? upload.user_has_liked) ? 'fill-current' : ''}`} />
                                                        <span>{uploadLikes[upload.id]?.count ?? upload.like_count ?? 0}</span>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const url = upload.file_url;
                                                            const path = new URL(url).pathname;
                                                            const pathParts = path.split('/').filter(p => p);
                                                            if (pathParts.length >= 4) {
                                                                window.open(`/view/${pathParts[1]}/${pathParts[2]}/${pathParts[3]}`, '_blank');
                                                            }
                                                        }}
                                                        className="text-gray-400 hover:text-green-400 transition-colors p-1"
                                                        title="Open with details"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                    </button>
                                                    {isOwnUpload(upload) && (
                                                        <button
                                                            onClick={() => handleDeleteClick(upload, 'audio')}
                                                            className="text-gray-400 hover:text-red-400 transition-colors p-1"
                                                            title="Delete audio"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <Music className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-400 mb-4">
                                        {isOwnProfile ? 'No music uploads yet' : 'No public music uploaded'}
                                    </p>
                                    {isOwnProfile && (
                                        <button
                                            className="inline-flex items-center gap-2 px-4 py-2 mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                            onClick={() => setShowMusicUploadModal(true)}
                                        >
                                            <Plus className="w-4 h-4" />
                                            Upload Your First Track
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* "View All Tracks" link */}
                            {musicUploads.length > 0 && (
                                <div className="mt-6 text-center">
                                    <Link
                                        to={`/feed/artist/${profile.username}/audio`}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-blue-600/20"
                                    >
                                        <Music className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">View All {musicUploads.length} Tracks</span>
                                        <span className="sm:hidden">{musicUploads.length} Tracks</span>
                                    </Link>
                                </div>
                            )}
                        </div>

                    {/* Video Uploads */}
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-white/10">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            <h2 className="text-xl sm:text-2xl font-bold text-white">
                                {isOwnProfile ? 'My Video Uploads' : 'Videos'}
                            </h2>
                            <div className="flex items-center gap-3">
                                {isOwnProfile && (
                                    <button
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                        onClick={() => setShowVideoUploadModal(true)}
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Add Video</span>
                                        <span className="sm:hidden">Add</span>
                                    </button>
                                )}
                            </div>
                        </div>
                            
                            {uploadsLoading ? (
                                <div className="text-center py-8">
                                    <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                                    <p className="text-gray-400">Loading uploads...</p>
                                </div>
                            ) : videoUploads.length > 0 ? (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {videoUploads.map((upload) => (
                                        <div key={upload.id} className="bg-gray-800/50 rounded-lg overflow-hidden hover:bg-gray-700/50 transition-colors">
                                            {/* Video Player */}
                                            <div className="relative">
                                                {isGated(upload.access_level, isAuthenticated, hasActiveSubscription) && !isOwnUpload(upload) ? (
                                                    <div className="relative overflow-hidden">
                                                        <video
                                                            src={upload.file_url}
                                                            poster={upload.cover_image_url}
                                                            title={upload.title || 'Video'}
                                                            className="w-full max-h-[300px] sm:max-h-[400px] lg:max-h-[600px] object-contain bg-gray-900 rounded-t-lg pointer-events-none"
                                                            preload="metadata"
                                                            playsInline
                                                        />
                                                        <button
                                                            onClick={() => setAccessModal({ isOpen: true, item: upload, mediaType: 'video' })}
                                                            aria-label="Protected content"
                                                            className="absolute inset-0 flex items-end justify-center pb-3 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
                                                        >
                                                            <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(upload.access_level, 'view')}</span>
                                                        </button>
                                                        <AccessLevelBadge accessLevel={upload.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                                    </div>
                                                ) : (
                                                <>
                                                <video
                                                    id={`video-${upload.id}`}
                                                    src={upload.file_url}
                                                    title={upload.title || 'Video'}
                                                    className="w-full max-h-[300px] sm:max-h-[400px] lg:max-h-[600px] object-contain bg-gray-900 rounded-t-lg"
                                                    onPlay={() => setCurrentlyPlaying(upload.id)}
                                                    onPause={() => setCurrentlyPlaying(null)}
                                                    onEnded={() => setCurrentlyPlaying(null)}
                                                    controls={currentlyPlaying === upload.id}
                                                    controlsList="nodownload"
                                                    playsInline
                                                    onLoadedMetadata={(e) => {
                                                        const dur = formatDuration(e.target.duration);
                                                        if (dur) setVideoDurations(prev => ({ ...prev, [upload.id]: dur }));
                                                    }}
                                                />
                                                {currentlyPlaying !== upload.id && (
                                                    <button
                                                        onClick={() => handleVideoPlay(upload)}
                                                        className="absolute inset-0 flex items-center justify-center transition-colors"
                                                    >
                                                        <Play className="w-12 h-12 text-white drop-shadow-lg" />
                                                    </button>
                                                )}
                                                </>
                                                )}
                                                
                                                {/* Open in New Tab Button */}
                                                <button
                                                    onClick={() => {
                                                        const url = upload.file_url;
                                                        const path = new URL(url).pathname;
                                                        const pathParts = path.split('/').filter(p => p);
                                                        // pathParts = ['uploads', 'videos', 'username', 'filename.mp4']
                                                        if (pathParts.length >= 4) {
                                                            const type = pathParts[1]; // 'videos'
                                                            const username = pathParts[2]; // 'username'  
                                                            const filename = pathParts[3]; // 'filename.mp4'
                                                            window.open(`/view/${type}/${username}/${filename}`, '_blank');
                                                        }
                                                    }}
                                                    className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 rounded-lg transition-colors"
                                                    title="Open video with details"
                                                >
                                                    <ExternalLink className="w-4 h-4 text-white" />
                                                </button>
                                            </div>
                                            
                                            {/* Video Info */}
                                            <div className="p-4">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-white font-semibold line-clamp-2">
                                                        {upload.title || upload.original_filename}
                                                    </h3>
                                                    {isTaggedUpload(upload) && (
                                                        <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                                            Contributor
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                                                    <span>{new Date(upload.created_at).toLocaleDateString()}</span>
                                                    {videoDurations[upload.id] && (
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {videoDurations[upload.id]}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        {/* Like */}
                                                        <button
                                                            onClick={() => handleUploadLike(upload)}
                                                            className={`flex items-center gap-1 text-sm transition-colors ${
                                                                (uploadLikes[upload.id]?.liked ?? upload.user_has_liked)
                                                                    ? 'text-red-400 hover:text-red-300'
                                                                    : 'text-gray-400 hover:text-red-400'
                                                            } cursor-pointer`}
                                                        >
                                                            <Heart className={`w-4 h-4 ${(uploadLikes[upload.id]?.liked ?? upload.user_has_liked) ? 'fill-current' : ''}`} />
                                                            <span>{uploadLikes[upload.id]?.count ?? upload.like_count ?? 0}</span>
                                                        </button>
                                                        {/* Share */}
                                                        <button
                                                            onClick={() => setMediaShareModal({ isOpen: true, upload, type: 'video' })}
                                                            className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-400 transition-colors"
                                                            title="Share"
                                                        >
                                                            <Share2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleVideoPlay(upload)}
                                                            className={`transition-colors ${
                                                                currentlyPlaying === upload.id
                                                                    ? 'text-blue-400'
                                                                    : 'text-gray-400 hover:text-blue-400'
                                                            }`}
                                                        >
                                                            {currentlyPlaying === upload.id ? (
                                                                <Pause className="w-4 h-4" />
                                                            ) : (
                                                                <Play className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                        {isOwnUpload(upload) && (
                                                            <button
                                                                onClick={() => handleDeleteClick(upload, 'video')}
                                                                className="text-gray-400 hover:text-red-400 transition-colors"
                                                                title="Delete video"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <Video className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-400 mb-4">
                                        {isOwnProfile ? 'No video uploads yet' : 'No public videos uploaded'}
                                    </p>
                                    {isOwnProfile && (
                                        <button
                                            className="inline-flex items-center gap-2 px-4 py-2 mt-4 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                            onClick={() => setShowVideoUploadModal(true)}
                                        >
                                            <Plus className="w-4 h-4" />
                                            Upload Your First Video
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* "View All Videos" link */}
                            {videoUploads.length > 0 && (
                                <div className="mt-6 text-center">
                                    <Link
                                        to={`/feed/artist/${profile.username}/videos`}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600/10 hover:bg-green-600/20 text-green-400 hover:text-green-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-green-600/20"
                                    >
                                        <Video className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">View All {videoUploads.length} Videos</span>
                                        <span className="sm:hidden">{videoUploads.length} Videos</span>
                                    </Link>
                                </div>
                            )}
                        </div>

                    {/* Image Uploads Section */}
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-white/10">
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                            <h2 className="text-xl sm:text-2xl font-bold text-white">
                                {isOwnProfile ? 'My Image Uploads' : 'Images'}
                            </h2>
                            {isOwnProfile && (
                                <button 
                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                    onClick={() => setShowImageUploadModal(true)}
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Add Image</span>
                                    <span className="sm:hidden">Add</span>
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {imageUploads.length > 0 ? (
                                imageUploads.slice(0, 6).map((upload, index) => (
                                    <div key={upload.id} className="glass-card overflow-hidden hover:scale-105 transition-all duration-300 group">
                                        {/* Image Display */}
                                        <div className="relative aspect-square bg-gray-900">
                                            <img
                                                src={upload.optimized_url || upload.file_url}
                                                alt={upload.title || upload.original_filename}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />

                                            {isGated(upload.access_level, isAuthenticated, hasActiveSubscription) && !isOwnUpload(upload) ? (
                                                /* Protected (non-owner): always-visible gradient overlay */
                                                <Link
                                                    to={`/view/image/${upload.username}/${upload.stored_filename || upload.original_filename || upload.id}`}
                                                    className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3"
                                                >
                                                    <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(upload.access_level, 'view')}</span>
                                                </Link>
                                            ) : (
                                                /* Public or owner: hover overlay with controls */
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                                                    <Link
                                                        to={`/view/image/${upload.username}/${upload.stored_filename || upload.original_filename || upload.id}`}
                                                        className="p-2 bg-blue-500/80 rounded-lg hover:bg-blue-600 transition-colors"
                                                        title="View Image"
                                                    >
                                                        <Eye className="w-4 h-4 text-white" />
                                                    </Link>
                                                    {isOwnUpload(upload) && (
                                                        <button
                                                            onClick={() => setDeleteModal({
                                                                show: true,
                                                                item: upload,
                                                                type: 'image'
                                                            })}
                                                            className="p-2 bg-red-500/80 rounded-lg hover:bg-red-600 transition-colors"
                                                            title="Delete Image"
                                                        >
                                                            <Trash2 className="w-4 h-4 text-white" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            <AccessLevelBadge accessLevel={upload.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />

                                            {/* File Size Badge */}
                                            <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                {upload.file_size && `${(upload.file_size / (1024 * 1024)).toFixed(1)} MB`}
                                            </div>
                                        </div>
                                        
                                        {/* Image Info */}
                                        <div className="p-3">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-semibold text-white text-sm truncate min-w-0">
                                                    {upload.title || upload.original_filename}
                                                </h3>
                                                {isTaggedUpload(upload) && (
                                                    <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                                        Contributor
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400 mb-2">
                                                {new Date(upload.created_at).toLocaleDateString()}
                                            </p>
                                            <div className="flex items-center gap-4">
                                                {/* Like */}
                                                <button
                                                    onClick={() => handleUploadLike(upload)}
                                                    className={`flex items-center gap-1 text-sm transition-colors ${
                                                        (uploadLikes[upload.id]?.liked ?? upload.user_has_liked)
                                                            ? 'text-red-400 hover:text-red-300'
                                                            : 'text-gray-400 hover:text-red-400'
                                                    } cursor-pointer`}
                                                >
                                                    <Heart className={`w-4 h-4 ${(uploadLikes[upload.id]?.liked ?? upload.user_has_liked) ? 'fill-current' : ''}`} />
                                                    <span>{uploadLikes[upload.id]?.count ?? upload.like_count ?? 0}</span>
                                                </button>
                                                {/* Share */}
                                                <button
                                                    onClick={() => setMediaShareModal({ isOpen: true, upload, type: 'image' })}
                                                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-400 transition-colors"
                                                    title="Share"
                                                >
                                                    <Share2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-full text-center py-12">
                                    <ImageIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-400 mb-4">
                                        {isOwnProfile ? 'No image uploads yet' : 'No public images uploaded'}
                                    </p>
                                    {isOwnProfile && (
                                        <button 
                                            className="inline-flex items-center gap-2 px-4 py-2 mt-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:scale-105"
                                            onClick={() => setShowImageUploadModal(true)}
                                        >
                                            <Plus className="w-4 h-4" />
                                            Upload Your First Image
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* "View All Images" link when there are more than 6 images */}
                        {imageUploads.length > 6 && (
                            <div className="mt-6 text-center">
                                <Link
                                    to={`/feed/artist/${profile.username}/images`}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-600/10 hover:bg-purple-600/20 text-purple-400 hover:text-purple-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-purple-600/20"
                                >
                                    <Eye className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">View All {imageUploads.length} Images</span>
                                    <span className="sm:hidden">{imageUploads.length} Images</span>
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Following - Show for own profile */}
                    {isOwnProfile && (
                        <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-white/10">
                            <div className="flex items-center justify-between mb-4 sm:mb-6">
                                <h2 className="text-xl sm:text-2xl font-bold text-white">Following</h2>
                                <button 
                                    onClick={() => setShowFollowingModal(true)}
                                    className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
                                >
                                    View All ({followingCount})
                                </button>
                            </div>
                            
                            {followingLoading ? (
                                <div className="text-center py-8">
                                    <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                    <p className="text-gray-400">Loading following...</p>
                                </div>
                            ) : followingList.length === 0 ? (
                                <div className="text-center py-12">
                                    <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-400 mb-4">No users followed yet</p>
                                    <Link 
                                        to="/community" 
                                        className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                                    >
                                        Discover creators to follow
                                    </Link>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {followingList.slice(0, 6).map((followedUser) => (
                                            <div key={followedUser.user_id} className="bg-gray-800/30 rounded-lg p-4 hover:bg-gray-800/50 transition-all duration-300">
                                                <Link to={`/profile/${followedUser.username}`} className="block">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        {followedUser.profile_picture_url ? (
                                                            <img
                                                                src={followedUser.profile_picture_url}
                                                                alt={followedUser.display_name}
                                                                width="48"
                                                                height="48"
                                                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                                                                style={{ background: 'transparent' }}
                                                            />
                                                        ) : (
                                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 border-2 border-gray-600 flex items-center justify-center">
                                                                <span className="text-white font-bold">
                                                                    {followedUser.display_name?.charAt(0).toUpperCase() || '🌊'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-semibold text-white truncate">
                                                                {followedUser.display_name}
                                                            </h3>
                                                            <p className="text-gray-400 text-sm truncate">
                                                                @{followedUser.username}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {followedUser.artist_verified ? (
                                                                <Verified className="w-4 h-4 text-blue-500" title="Verified Artist" />
                                                            ) : null}
                                                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                                        </div>
                                                    </div>
                                                    
                                                    {followedUser.bio && (
                                                        <p className="text-gray-400 text-sm line-clamp-2 mb-3">
                                                            {followedUser.bio}
                                                        </p>
                                                    )}
                                                    
                                                    {followedUser.crew_role && (
                                                        <div className="text-xs">
                                                            <span className="text-yellow-400 font-semibold">
                                                                {followedUser.crew_role}
                                                            </span>
                                                        </div>
                                                    )}
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {followingList.length > 6 && (
                                        <div className="text-center pt-4">
                                            <button
                                                onClick={() => setShowFollowingModal(true)}
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600/10 hover:bg-purple-600/20 text-purple-400 hover:text-purple-300 rounded-lg transition-all duration-200 hover:scale-105 border border-purple-600/20 font-medium"
                                            >
                                                View All {followingCount} Following
                                                <Users className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Liked Media - Show for all users */}
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-3 sm:p-6 border-y sm:border border-white/10">
                        <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Liked Media</h2>
                        {likedMediaLoading ? (
                            <div className="text-center py-8">
                                <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                                <p className="text-gray-400">Loading liked media...</p>
                            </div>
                        ) : likedMedia.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {likedMedia.map((item) => {
                                    const viewPath = `/view/${item.file_type}/${item.username}/${item.stored_filename || item.original_filename || item.id}`;
                                    const TypeIcon = item.file_type === 'audio' ? Music : item.file_type === 'video' ? Video : ImageIcon;
                                    const thumbUrl = item.cover_image_url || (item.file_type === 'image' ? (item.optimized_url || item.file_url) : null);
                                    return (
                                        <Link
                                            key={item.id}
                                            to={viewPath}
                                            className="flex items-center gap-3 bg-gray-800/50 rounded-xl p-3 hover:bg-gray-700/50 transition-colors"
                                        >
                                            <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-900">
                                                {thumbUrl ? (
                                                    <img
                                                        src={thumbUrl}
                                                        alt={item.title || item.original_filename}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <TypeIcon className="w-6 h-6 text-gray-500" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white font-medium text-sm truncate">
                                                    {item.title || item.original_filename}
                                                </p>
                                                <p className="text-gray-500 text-xs truncate">
                                                    by {item.display_name || item.username}
                                                </p>
                                            </div>
                                            <TypeIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                        </Link>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                <p className="text-gray-400">No liked media yet</p>
                            </div>
                        )}

                        {/* "View All" link */}
                        {likedMediaTotal > 6 && (
                            <div className="mt-6 text-center">
                                <Link
                                    to={`/profile/${profile.username}/liked`}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-600/10 hover:bg-pink-600/20 text-pink-400 hover:text-pink-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-pink-600/20"
                                >
                                    <Heart className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">View All {likedMediaTotal} Liked</span>
                                    <span className="sm:hidden">{likedMediaTotal} Liked</span>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Links Management Modal */}
            {showLinksModal && (
                <LinksModal
                    isOpen={showLinksModal}
                    onClose={() => setShowLinksModal(false)}
                    onSave={saveProfileLinks}
                    currentLinks={profileLinks}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold text-white mb-4">
                            Delete {deleteModal.type === 'audio' ? 'Audio' : deleteModal.type === 'video' ? 'Video' : 'Image'}
                        </h3>
                        <p className="text-gray-300 mb-6">
                            Are you sure you want to permanently delete "{deleteModal.item?.title || deleteModal.item?.original_filename}"? 
                            This action cannot be undone and will remove the file from the server.
                        </p>
                        <div className="flex gap-4 justify-end">
                            <button
                                onClick={handleDeleteCancel}
                                disabled={deleting}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Delete Permanently
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Music Upload Modal */}
            <MusicUploadModal
                isOpen={showMusicUploadModal}
                onClose={() => setShowMusicUploadModal(false)}
                onSuccess={handleMusicUploadSuccess}
            />

            {/* Video Upload Modal */}
            <VideoUploadModal
                isOpen={showVideoUploadModal}
                onClose={() => setShowVideoUploadModal(false)}
                onSuccess={handleVideoUploadSuccess}
            />

            {/* Image Upload Modal */}
            <ImageUploadModal
                isOpen={showImageUploadModal}
                onClose={() => setShowImageUploadModal(false)}
                onSuccess={handleImageUploadSuccess}
            />

            {/* Share Modal - Profile */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, profile: null })}
                mediaTitle={shareModal.profile?.displayName || shareModal.profile?.username}
                mediaType="profile"
                username={shareModal.profile?.username}
                mediaId={shareModal.profile?.username}
                mediaUrl={`${window.location.origin}/profile/${shareModal.profile?.username}`}
            />

            {/* Share Modal - Media (video / image) */}
            <ShareModal
                isOpen={mediaShareModal.isOpen}
                onClose={() => setMediaShareModal({ isOpen: false, upload: null, type: null })}
                mediaTitle={mediaShareModal.upload?.title || mediaShareModal.upload?.original_filename || ''}
                mediaType={mediaShareModal.type || 'content'}
                username={profile?.username}
                mediaId={mediaShareModal.upload?.stored_filename || mediaShareModal.upload?.original_filename || mediaShareModal.upload?.id}
                artistName={profile?.displayName || profile?.username}
            />

            {/* Share Modal - Albums */}
            <ShareModal
                isOpen={albumShareModal.isOpen}
                onClose={() => setAlbumShareModal({ isOpen: false, album: null })}
                mediaTitle={albumShareModal.album?.title || ''}
                mediaType="album"
                mediaUrl={albumShareModal.album ? `${window.location.origin}/album/${albumShareModal.album.username}/${albumShareModal.album.slug || albumShareModal.album.id}` : null}
                username={albumShareModal.album?.username}
                artistName={albumShareModal.album?.display_name || albumShareModal.album?.username}
                imageUrl={albumShareModal.album?.cover_image_url}
                description={albumShareModal.album?.description}
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, item: null, mediaType: 'audio' })}
                accessLevel={accessModal.item?.access_level}
                action={accessModal.mediaType === 'video' ? 'view' : 'listen'}
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.item ? `/view/${accessModal.mediaType}/${accessModal.item.username}/${accessModal.item.stored_filename || accessModal.item.original_filename || accessModal.item.id}` : null}
            />

            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'content' })}
                contentType={signupInvite.contentType}
            />
        </>
    );
};

export default ProfilePage;